import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Declare jwt_decode as global function
declare const jwt_decode: (token: string) => any;

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token?: string;
    token_type: string;
    expires_in: number;
    user: {
      id: number;
      username: string;
      status: string;
      employee: {
        id: number;
        name: string;
        employee_code: string;
        email: string;
        mobile_number: string;
        profile_photo?: string;
        role: {
          id: number;
          name: string;
          slug: string;
        };
      };
    };
    privileges?: string[]; // Array of permission keys (e.g., ["employeeManagement.view", "machines.add"])
  };
}

export interface User {
  id: number;
  username: string;
  status: string;
  employee: {
    id: number;
    name: string;
    employee_code: string;
    email: string;
    mobile_number: string;
    profile_photo?: string;
    role: {
      id: number;
      name: string;
      slug: string;
    };
  };
}

export interface JwtPayload {
  iss: string;
  iat: number;
  exp: number;
  nbf: number;
  jti: string;
  sub: string;
  prv: string;
  user: User;
  privileges?: string[]; // Privileges may be stored in token
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';
  private readonly PRIVILEGES_KEY = 'user_privileges';
  
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  private privilegesSubject = new BehaviorSubject<string[]>([]);
  public privileges$ = this.privilegesSubject.asObservable();
  
  private loginCompleteSubject = new BehaviorSubject<boolean>(false);
  public loginComplete$ = this.loginCompleteSubject.asObservable();
  
  private refreshTimer: any = null;

  constructor(private http: HttpClient) {
    // Initialize user from localStorage on service creation
    this.initializeUser();
    // Initialize privileges from localStorage
    this.initializePrivileges();
  }

  /**
   * Login user with username and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiBaseUrl}/login`, credentials, {
      withCredentials: false
    })
      .pipe(
        tap(response => {
          if (response.success && response.data && response.data.access_token) {
            // Store the access token first
            this.setToken(response.data.access_token);
            
            // Store refresh token if available
            if (response.data.refresh_token) {
              this.setRefreshToken(response.data.refresh_token);
            }
            
            // Store token expiry
            if (response.data.expires_in) {
              this.setTokenExpiry(response.data.expires_in);
            }
            
            // Store user data from the response
            if (response.data.user) {
              this.setUser(response.data.user);
            } else {
              // Fallback: Extract user data from JWT token
              const tokenData = this.decodeToken(response.data.access_token);
              if (tokenData && tokenData.user) {
                this.setUser(tokenData.user);
              }
            }
            
            // Store privileges from the response
            if (response.data.privileges && Array.isArray(response.data.privileges)) {
              this.setPrivileges(response.data.privileges);
            }
            
            // Schedule automatic token refresh
            this.scheduleTokenRefresh();
            
            // Emit login complete signal after all data is set
            this.loginCompleteSubject.next(true);
          }
        })
      );
  }

  /**
   * Logout user and clear stored data
   */
  logout(): void {
    // Clear all localStorage data
    localStorage.clear();
    this.currentUserSubject.next(null);
    this.privilegesSubject.next([]);
    this.loginCompleteSubject.next(false); // Reset login complete flag
    this.clearRefreshTimer();
  }

  /**
   * Handle authentication error and logout
   */
  handleAuthError(error?: any): void {
    if (error) {
      console.error('Authentication error details:', error);
    }
    
    // Clear all authentication data
    this.logout();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    
    // Must have both tokens
    if (!token || !refreshToken) {
      return false;
    }
    
    // Check if token is expired
    try {
      const tokenData = this.decodeToken(token);
      if (tokenData && tokenData.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return tokenData.exp > currentTime;
      }
    } catch (error) {
      console.error('Error validating token:', error);
    }
    
    return false;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }
    
    try {
      const tokenData = this.decodeToken(token);
      if (tokenData && tokenData.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return tokenData.exp <= currentTime;
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
    }
    
    return true;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get user's display name
   */
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    return user ? user.employee.name : 'User';
  }

  /**
   * Get user's profile image URL
   */
  getUserProfileImage(): string {
    const user = this.getCurrentUser();
    return user && user.employee.profile_photo ? user.employee.profile_photo : 'assets/images/user/avatar-1.jpg';
  }

  /**
   * Get user's employee code
   */
  getUserEmployeeCode(): string {
    const user = this.getCurrentUser();
    return user ? user.employee.employee_code : '';
  }

  /**
   * Get user's role name
   */
  getUserRoleName(): string {
    const user = this.getCurrentUser();
    return user ? user.employee.role.name : '';
  }

  /**
   * Set privileges and store in localStorage
   */
  private setPrivileges(privileges: string[]): void {
    this.privilegesSubject.next(privileges);
    localStorage.setItem(this.PRIVILEGES_KEY, JSON.stringify(privileges));
  }

  /**
   * Get current user privileges
   */
  getPrivileges(): string[] {
    return this.privilegesSubject.value;
  }

  /**
   * Check if user has a specific privilege
   * @param privilege The privilege key to check (e.g., "employeeManagement.view" or "machines.add")
   */
  hasPrivilege(privilege: string): boolean {
    const privileges = this.getPrivileges();
    return privileges.includes(privilege);
  }

  /**
   * Check if user has any of the specified privileges
   * @param privilegeKeys Array of privilege keys to check
   */
  hasAnyPrivilege(privilegeKeys: string[]): boolean {
    const privileges = this.getPrivileges();
    return privilegeKeys.some(key => privileges.includes(key));
  }

  /**
   * Check if user has all of the specified privileges
   * @param privilegeKeys Array of privilege keys to check
   */
  hasAllPrivileges(privilegeKeys: string[]): boolean {
    const privileges = this.getPrivileges();
    return privilegeKeys.every(key => privileges.includes(key));
  }

  /**
   * Initialize privileges from localStorage
   */
  private initializePrivileges(): void {
    const storedPrivileges = localStorage.getItem(this.PRIVILEGES_KEY);
    if (storedPrivileges) {
      try {
        const privileges = JSON.parse(storedPrivileges);
        if (Array.isArray(privileges) && privileges.length > 0) {
          this.privilegesSubject.next(privileges);
          // If privileges exist and user is authenticated, set login complete
          if (this.isAuthenticated()) {
            this.loginCompleteSubject.next(true);
          }
        }
      } catch (error) {
        console.error('Error parsing stored privileges:', error);
        this.privilegesSubject.next([]);
      }
    }
  }

  /**
   * Refresh user data from current JWT token
   */
  refreshUserFromToken(): void {
    const token = this.getToken();
    if (token && this.isAuthenticated()) {
      try {
        const tokenData = this.decodeToken(token);
        if (tokenData && tokenData.user) {
          this.setUser(tokenData.user);
        }
        // Also refresh privileges from token if available
        if (tokenData && tokenData.privileges && Array.isArray(tokenData.privileges)) {
          this.setPrivileges(tokenData.privileges);
        }
      } catch (error) {
        console.error('Error refreshing user from token:', error);
      }
    }
  }

  /**
   * Refresh privileges from current JWT token
   * This extracts privileges directly from the token, similar to how dashboard calls APIs
   */
  refreshPrivilegesFromToken(): void {
    const token = this.getToken();
    if (token && this.isAuthenticated()) {
      try {
        const tokenData = this.decodeToken(token);
        if (tokenData && tokenData.privileges && Array.isArray(tokenData.privileges)) {
          // Privileges found in token, update them
          this.setPrivileges(tokenData.privileges);
        } else {
          // If no privileges in token, check localStorage as fallback
          const storedPrivileges = localStorage.getItem(this.PRIVILEGES_KEY);
          if (storedPrivileges) {
            try {
              const privileges = JSON.parse(storedPrivileges);
              if (Array.isArray(privileges) && privileges.length > 0) {
                this.privilegesSubject.next(privileges);
              }
            } catch (error) {
              console.error('Error parsing stored privileges:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing privileges from token:', error);
      }
    }
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Set token in localStorage
   */
  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Set user data in BehaviorSubject
   */
  private setUser(user: User): void {
    this.currentUserSubject.next(user);
  }

  /**
   * Set refresh token in localStorage
   */
  private setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  /**
   * Set token expiry time in localStorage
   */
  private setTokenExpiry(expiresIn: number): void {
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  /**
   * Get refresh token from localStorage
   */
  private getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get token expiry time from localStorage
   */
  private getTokenExpiry(): number | null {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  /**
   * Refresh access token using refresh token
   */
  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    const currentToken = this.getToken();
    
    // Only refresh if both tokens are available
    if (!refreshToken || !currentToken) {
      return throwError('No refresh token or access token available');
    }

    return this.http.post<LoginResponse>(`${environment.apiBaseUrl}/refresh`, {
      refresh_token: refreshToken
    }).pipe(
      tap(response => {
        if (response.success && response.data && response.data.access_token) {
          this.setToken(response.data.access_token);
          this.setTokenExpiry(response.data.expires_in);
          
          if (response.data.user) {
            this.setUser(response.data.user);
          }
          
          // Schedule next refresh
          this.scheduleTokenRefresh();
        } else {
        }
      }),
      catchError(error => {
        console.error('❌ Refresh Token API Error at:', new Date().toLocaleString());
        console.error('   Error Details:', error);
        // If refresh fails, logout user
        this.logout();
        return throwError(error);
      })
    );
  }

  /**
   * Schedule automatic token refresh 30 seconds before expiry
   */
  private scheduleTokenRefresh(): void {
    this.clearRefreshTimer();
    
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    
    // Only schedule refresh if both tokens are available
    if (!token || !refreshToken) {
      return;
    }
    
    try {
      // Decode JWT token to get expiry time
      const tokenData = this.decodeToken(token);
      if (!tokenData || !tokenData.exp) {
        return;
      }
      
      // Convert JWT exp (seconds) to milliseconds
      const expiryTime = tokenData.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;
      const refreshTime = timeUntilExpiry - 30000; // 30 seconds before expiry
      
      if (refreshTime > 0) {
        this.refreshTimer = setTimeout(() => {
          this.refreshToken().subscribe({
            next: () => {
            },
            error: (error) => {
              console.error('❌ Automatic token refresh failed at:', new Date().toLocaleString(), error);
              this.logout();
            }
          });
        }, refreshTime);
        
      } else {
      }
    } catch (error) {
      console.error('❌ Error scheduling token refresh:', error);
    }
  }

  /**
   * Clear the refresh timer
   */
  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Initialize user from JWT token
   */
  private initializeUser(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    
    // Only proceed if both tokens are available
    if (!token || !refreshToken) {
      return;
    }
    
    try {
      // Check if token is expired
      const tokenData = this.decodeToken(token);
      if (!tokenData || !tokenData.exp) {
        this.logout();
        return;
      }
      
      // Check if token is expired (exp is in seconds)
      const now = Math.floor(Date.now() / 1000);
      
      if (tokenData.exp <= now) {
        // Token is expired, try to refresh
        this.refreshToken().subscribe({
          next: () => {
          },
          error: (error) => {
            console.error('❌ Failed to refresh expired token on initialization:', error);
            this.logout();
          }
        });
        return;
      }
      
      // Token is valid, set user data
      if (tokenData.user) {
        this.setUser(tokenData.user);
      }
      
      // Schedule token refresh
      this.scheduleTokenRefresh();
      
    } catch (error) {
      console.error('❌ Error initializing user:', error);
      this.logout();
    }
  }

  /**
   * Decode JWT token using jwt-decode package
   */
  private decodeToken(token: string): JwtPayload | null {
    try {
      // Use global jwt_decode function
      if (typeof jwt_decode !== 'undefined') {
        return jwt_decode(token) as JwtPayload;
      }
      
      // Fallback to manual JWT decoding
      return this.manualJwtDecode(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Manual JWT decoder with improved error handling
   */
  private manualJwtDecode(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if needed
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      const jsonPayload = decodeURIComponent(
        atob(padded)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (error) {
      console.error('Error in manual JWT decode:', error);
      return null;
    }
  }
}
